/**
 * Who may see what inside one enterprise. One table, shared by the API (which enforces it), the
 * PC admin (menus and routes) and the mini program (which home a member lands on).
 *
 * Roles are fixed; a member may hold several and gets the union of their permissions. Writes
 * keep their own checks next to each action (`permit` in the API, by role, since the same action
 * can depend on which side of an order the enterprise is on). The permissions here govern what a
 * member can open and read, including whether money is shown at all.
 */
export const ROLES = ['admin', 'sales', 'purchase', 'warehouse', 'finance', 'viewer'];
export const roleNames = {
    admin: '企业管理员',
    sales: '销售',
    purchase: '采购',
    warehouse: '仓管',
    finance: '对账',
    viewer: '只读',
};
/** One line per role, shown where an administrator picks roles for a member. */
export const roleHints = {
    admin: '全部功能，含成员与操作记录',
    sales: '销售单与客户，看销售价',
    purchase: '采购单与供应商，看采购价',
    warehouse: '备货发货、收货入库与库存，看不到任何金额',
    finance: '对账结算、收付款与交易统计',
    viewer: '查看全部数据，不能修改',
};
/** Roles offered when adding or editing a member; 只读 stays valid for existing members. */
export const ASSIGNABLE_ROLES = ['admin', 'sales', 'purchase', 'warehouse', 'finance'];
const ALL = [
    'sales',
    'purchase',
    'warehouse',
    'inventory',
    'products',
    'partners',
    'materials',
    'statements',
    'stats',
    'settings',
    'members',
    'price.sale',
    'price.purchase',
];
export const ROLE_PERMISSIONS = {
    admin: ALL,
    viewer: ALL.filter((p) => p !== 'members'),
    sales: ['sales', 'inventory', 'products', 'partners', 'materials', 'price.sale'],
    purchase: ['purchase', 'inventory', 'products', 'partners', 'materials', 'price.purchase'],
    // Only 仓库作业 and 库存 (warehouses themselves are kept under 库存). The item master is
    // maintained by those who sell and buy the items, so 商品档案 is not the warehouse's.
    warehouse: ['warehouse', 'inventory'],
    finance: [
        'sales',
        'purchase',
        'statements',
        'stats',
        'partners',
        'settings',
        'price.sale',
        'price.purchase',
    ],
};
export function permissionsOf(roles) {
    const set = new Set();
    for (const role of roles)
        for (const p of ROLE_PERMISSIONS[role] ?? [])
            set.add(p);
    return ALL.filter((p) => set.has(p));
}
export const can = (roles, permission) => roles.some((role) => ROLE_PERMISSIONS[role]?.includes(permission));
/** Whether a member may open orders on this side: their own trade, or to ship and receive. */
export const canSeeSide = (roles, side) => can(roles, side === 'SELL' ? 'sales' : 'purchase') || can(roles, 'warehouse');
/** Whether prices and amounts of orders on this side are shown to the member. */
export const canSeePrice = (roles, side) => can(roles, side === 'SELL' ? 'price.sale' : 'price.purchase');
/** No money anywhere: the member is warehouse staff only (or holds no priced role). */
export const seesNoMoney = (roles) => !can(roles, 'price.sale') && !can(roles, 'price.purchase');
/**
 * Where a member works day to day. Warehouse staff without any office role get the warehouse
 * workbench (PC) and the scan-to-ship home (mini program) instead of the trade workbench.
 */
export const workbenchOf = (roles) => can(roles, 'warehouse') && !can(roles, 'sales') && !can(roles, 'purchase')
    ? 'warehouse'
    : 'office';
export const roleLabel = (roles) => ROLES.filter((r) => roles.includes(r))
    .map((r) => roleNames[r])
    .join('、');
